import { useState, useEffect, useMemo, useRef } from 'react';
import { Coins, Gem, BarChart3, Users, AlertCircle, TrendingUp, TrendingDown, RefreshCw, Gamepad2, Activity, Server, Scale, PiggyBank, Wallet, UserPlus, Clock, ChevronDown, ChevronUp, type LucideIcon } from 'lucide-react';
import { apiService, EconomyCurrentResponse, EconomySnapshotDTO, EconomyBreakdownResponse, StorePointsCurrentResponse, StorePointsSnapshotDTO, DailyTransactionStatsDTO, PlayerActivityResponse, PlayerStatsResponse, ServerStatsDataPoint, DaySeriesDTO } from '@/services/api.service';
import { StackedBarChart } from '@/components/StackedBarChart';
import { ServerLineChart } from './PlayerStatsPage';
import styles from './StatsPage.module.scss';

/** Marqueur dans la raison des transactions comptabilisées comme give/clear staff. */
const STATS_REASON = '[stats]';

type StatsSection = 'economie' | 'points' | 'gameplay' | 'activite' | 'infrastructure';

interface SectionConfig {
  id: StatsSection;
  label: string;
  icon: LucideIcon;
}

const sections: SectionConfig[] = [
  { id: 'economie', label: 'Economie', icon: Coins },
  { id: 'points', label: 'Points Boutique', icon: Gem },
  { id: 'gameplay', label: 'Game Play', icon: Gamepad2 },
  { id: 'activite', label: 'Activité player', icon: Activity },
  { id: 'infrastructure', label: 'Infrastructure', icon: Server },
];

export function StatsPage() {
  const [section, setSection] = useState<StatsSection>('economie');

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Statistiques</h1>
          <p className={styles.subtitle}>Analysez les données du serveur</p>
        </div>
      </header>

      <div className={styles.content}>
        {/* Sections Navigation */}
        <nav className={styles.tabs}>
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                className={`${styles.tab} ${section === s.id ? styles.tabActive : ''}`}
                onClick={() => setSection(s.id)}
              >
                <span className={styles.tabIcon}><Icon size={18} strokeWidth={2} /></span>
                <span className={styles.tabLabel}>{s.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Section Content */}
        <div className={styles.tabContent}>
          {section === 'economie' && <EconomieSection />}
          {section === 'points' && <StorePointsTab />}
          {section === 'gameplay' && <ComingSoon title="Game Play" icon={Gamepad2} />}
          {section === 'activite' && <ActiviteSection />}
          {section === 'infrastructure' && <ComingSoon title="Infrastructure" icon={Server} />}
        </div>
      </div>
    </div>
  );
}

// ==================== PLACEHOLDERS ====================

function ComingSoon({ title, icon: Icon }: { title: string; icon: LucideIcon }) {
  return (
    <div className={styles.comingSoon}>
      <Icon size={48} strokeWidth={1.5} />
      <h3>{title}</h3>
      <p>Section en construction — à définir prochainement.</p>
    </div>
  );
}

// ==================== ACTIVITÉ PLAYER ====================

type ActivityRange = 7 | 14 | 30 | 60 | 'custom';

const CURVE_COLORS = ['#8B5CF6', '#10B981', '#EF4444', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'];

/** Famille d'un serveur = préfixe alphabétique (minage-1 -> minage, lobby-3 -> lobby). */
function familyOf(server: string): string {
  const m = server.toLowerCase().match(/^([a-z]+)/);
  return m && m[1] ? m[1] : server.toLowerCase();
}

/** Somme de plusieurs séries par timestamp (agrégation d'une famille). */
function mergeSeries(seriesList: ServerStatsDataPoint[][]): ServerStatsDataPoint[] {
  const byTs = new Map<number, { avg: number; min: number; max: number }>();
  for (const series of seriesList) {
    for (const p of series) {
      const e = byTs.get(p.timestamp) ?? { avg: 0, min: 0, max: 0 };
      e.avg += p.avg;
      e.min += p.min;
      e.max += p.max;
      byTs.set(p.timestamp, e);
    }
  }
  return [...byTs.entries()].sort((a, b) => a[0] - b[0]).map(([timestamp, v]) => ({ timestamp, ...v }));
}

/** Durée valide (minutes) pour getPlayerStats selon un nombre de jours. */
function durationForDays(days: number): number {
  if (days <= 7) return 10080;
  if (days <= 14) return 20160;
  if (days <= 30) return 43200;
  if (days <= 60) return 86400;
  if (days <= 90) return 129600;
  if (days <= 182) return 262800;
  return 525600;
}

function makeFormatTime(durationMinutes: number) {
  return (ts: number): string => {
    const d = new Date(ts);
    if (durationMinutes <= 1440) {
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };
}

/** Convertit une série journalière en points de courbe (une valeur par jour). */
function activityToPoints(series: DaySeriesDTO[], getValue: (d: DaySeriesDTO) => number): ServerStatsDataPoint[] {
  return series.map((d) => {
    const v = getValue(d);
    return { timestamp: new Date(d.date).getTime(), avg: v, min: v, max: v };
  });
}

function ActivityCurveCard({
  label,
  color,
  points,
  formatTime,
  onHover,
}: {
  label: string;
  color: string;
  points: ServerStatsDataPoint[];
  formatTime: (ts: number) => string;
  onHover: (point: ServerStatsDataPoint | null, x: number, y: number) => void;
}) {
  return (
    <div className={styles.curveCard}>
      <div className={styles.curveHeader}>
        <span className={styles.curveName}>
          <span className={styles.curveDot} style={{ background: color }} />
          {label}
        </span>
        <span className={styles.curveValue}>{points[points.length - 1]?.avg ?? 0} actuellement</span>
      </div>
      <ServerLineChart data={points} color={color} isAggregated={false} formatTime={formatTime} onHover={onHover} />
    </div>
  );
}

/** Courbes des joueurs en ligne : total + par famille (clic = détail par serveur). */
interface CurveHover {
  x: number;
  y: number;
  point: ServerStatsDataPoint;
  label: string;
  unit: string;
  aggregated: boolean;
  daily: boolean;
}

function OnlineCurves({ durationMinutes, activitySeries }: { durationMinutes: number; activitySeries: DaySeriesDTO[] }) {
  const [stats, setStats] = useState<PlayerStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [hover, setHover] = useState<CurveHover | null>(null);

  const onHover = (label: string, aggregated: boolean, unit: string, daily = false) => (point: ServerStatsDataPoint | null, x: number, y: number) =>
    setHover(point ? { x, y, point, label, aggregated, unit, daily } : null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService.getPlayerStats(durationMinutes).then((r) => {
      if (cancelled) return;
      setStats(r.data ?? null);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [durationMinutes]);

  const isAggregated = durationMinutes > 180;
  const formatTime = makeFormatTime(durationMinutes);
  // Les courbes d'activité sont toujours par jour (jamais par heure).
  const formatDay = (ts: number) => new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

  const totalSeries: ServerStatsDataPoint[] = (stats?.globalStats ?? []).map((d) => ({
    timestamp: d.timestamp,
    avg: d.totalPlayersAvg,
    min: d.totalPlayersMin,
    max: d.totalPlayersMax,
  }));

  const families = useMemo(() => {
    const map: Record<string, string[]> = {};
    if (stats) {
      for (const server of Object.keys(stats.serverStats)) {
        const fam = familyOf(server);
        (map[fam] ??= []).push(server);
      }
    }
    return map;
  }, [stats]);

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <span>Chargement des courbes...</span>
      </div>
    );
  }

  if (!stats || totalSeries.length === 0) {
    return (
      <div className={styles.noData}>
        <p>Aucune donnée de fréquentation pour cette période.</p>
      </div>
    );
  }

  const familyNames = Object.keys(families).sort();

  return (
    <div className={styles.curvesSection}>
      <h3 className={styles.subChartTitle}>
        <span className={styles.chartIcon}><Activity size={16} strokeWidth={2} /></span>
        Joueurs en ligne
      </h3>

      {/* Total */}
      <div className={styles.curveCard}>
        <div className={styles.curveHeader}>
          <span className={styles.curveName}>
            <span className={styles.curveDot} style={{ background: '#3B82F6' }} />
            Total
          </span>
          <span className={styles.curveValue}>{totalSeries[totalSeries.length - 1]?.avg ?? 0} actuellement</span>
        </div>
        <ServerLineChart data={totalSeries} color="#3B82F6" isAggregated={isAggregated} formatTime={formatTime} onHover={onHover('Total', isAggregated, 'joueurs')} />
      </div>

      {/* Bouton pour dérouler le détail par famille */}
      {familyNames.length > 0 && (
        <button className={styles.detailToggle} onClick={() => setShowDetail((v) => !v)}>
          {showDetail ? <ChevronUp size={16} strokeWidth={2} /> : <ChevronDown size={16} strokeWidth={2} />}
          {showDetail ? 'Masquer le détail par famille' : `Voir le détail par famille (${familyNames.length})`}
        </button>
      )}

      {/* Par famille (clic = détail par serveur) */}
      {showDetail && familyNames.map((fam, i) => {
        const color = CURVE_COLORS[i % CURVE_COLORS.length]!;
        const servers = families[fam]!;
        const famSeries = mergeSeries(servers.map((s) => stats.serverStats[s] ?? []));
        const isOpen = expanded === fam;
        return (
          <div key={fam} className={styles.curveCard}>
            <button className={styles.curveHeaderBtn} onClick={() => setExpanded(isOpen ? null : fam)}>
              <span className={styles.curveName}>
                <span className={styles.curveDot} style={{ background: color }} />
                <span className={styles.curveFamily}>{fam}</span>
                <span className={styles.curveMeta}>{servers.length} serveur{servers.length > 1 ? 's' : ''}</span>
              </span>
              <span className={styles.curveValue}>
                {famSeries[famSeries.length - 1]?.avg ?? 0} · {isOpen ? 'masquer' : 'détail'}
              </span>
            </button>
            <ServerLineChart data={famSeries} color={color} isAggregated={isAggregated} formatTime={formatTime} onHover={onHover(fam, isAggregated, 'joueurs')} />
            {isOpen && (
              <div className={styles.curveDetail}>
                {servers.slice().sort().map((s, j) => {
                  const c = CURVE_COLORS[(i + j + 1) % CURVE_COLORS.length]!;
                  const serie = stats.serverStats[s] ?? [];
                  return (
                    <div key={s} className={styles.curveSubCard}>
                      <div className={styles.curveHeader}>
                        <span className={styles.curveName}>
                          <span className={styles.curveDot} style={{ background: c }} />
                          {s}
                        </span>
                        <span className={styles.curveValue}>{serie[serie.length - 1]?.avg ?? 0}</span>
                      </div>
                      <ServerLineChart data={serie} color={c} isAggregated={isAggregated} formatTime={formatTime} onHover={onHover(s, isAggregated, 'joueurs')} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Courbes d'activité (nouveaux, uniques, temps de jeu) */}
      {activitySeries.length > 0 && (
        <>
          <ActivityCurveCard label="Nouveaux joueurs" color="#22C55E" points={activityToPoints(activitySeries, (d) => d.newPlayers)} formatTime={formatDay} onHover={onHover('Nouveaux joueurs', false, 'joueurs', true)} />
          <ActivityCurveCard label="Joueurs uniques" color="#3B82F6" points={activityToPoints(activitySeries, (d) => d.uniquePlayers)} formatTime={formatDay} onHover={onHover('Joueurs uniques', false, 'joueurs', true)} />
          <ActivityCurveCard label="Temps de jeu (h)" color="#F59E0B" points={activityToPoints(activitySeries, (d) => Math.round(d.minutes / 60))} formatTime={formatDay} onHover={onHover('Temps de jeu (h)', false, 'h', true)} />
        </>
      )}

      <DailyDistribution stats={stats} familyNames={familyNames} />

      {hover && (
        <div className={styles.curveTooltip} style={{ left: hover.x + 14, top: hover.y + 14 }}>
          <div className={styles.curveTooltipLabel}>{hover.label}</div>
          <div className={styles.curveTooltipTime}>
            {hover.daily
              ? new Date(hover.point.timestamp).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
              : new Date(hover.point.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </div>
          {hover.aggregated ? (
            <>
              <div className={styles.curveTooltipRow}><span>Moyenne</span><strong>{hover.point.avg} {hover.unit}</strong></div>
              <div className={styles.curveTooltipRow}><span>Min / Max</span><strong>{hover.point.min} / {hover.point.max}</strong></div>
            </>
          ) : (
            <div className={styles.curveTooltipRow}><span>{hover.label}</span><strong>{hover.point.avg} {hover.unit}</strong></div>
          )}
        </div>
      )}
    </div>
  );
}

/** Répartition quotidienne des joueurs par famille (barres empilées à 100%). */
interface DistHover {
  x: number;
  y: number;
  date: string;
  fam: string;
  pct: number;
  value: number;
}

function DailyDistribution({ stats, familyNames }: { stats: PlayerStatsResponse; familyNames: string[] }) {
  const colorFor = (fam: string) => CURVE_COLORS[Math.max(0, familyNames.indexOf(fam)) % CURVE_COLORS.length]!;
  const [hover, setHover] = useState<DistHover | null>(null);

  const days = useMemo(() => {
    const dayMap = new Map<string, Map<string, number>>();
    for (const [server, series] of Object.entries(stats.serverStats)) {
      const fam = familyOf(server);
      for (const p of series) {
        const day = formatYMD(new Date(p.timestamp));
        let fm = dayMap.get(day);
        if (!fm) {
          fm = new Map();
          dayMap.set(day, fm);
        }
        fm.set(fam, (fm.get(fam) ?? 0) + p.avg);
      }
    }
    return [...dayMap.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, fm]) => {
        const total = [...fm.values()].reduce((a, b) => a + b, 0);
        const segments = familyNames
          .map((fam) => ({ fam, value: fm.get(fam) ?? 0 }))
          .filter((s) => s.value > 0)
          .map((s) => ({ ...s, pct: total > 0 ? (s.value / total) * 100 : 0 }));
        return { date, total, segments };
      })
      .filter((d) => d.total > 0);
  }, [stats, familyNames]);

  if (days.length === 0) return null;
  const labelEvery = days.length > 14 ? Math.ceil(days.length / 10) : 1;

  return (
    <div className={styles.distSection}>
      <h3 className={styles.subChartTitle}>
        <span className={styles.chartIcon}><BarChart3 size={16} strokeWidth={2} /></span>
        Répartition par jour (où sont les joueurs)
      </h3>

      <div className={styles.distBars}>
        {days.map((d, idx) => (
          <div key={d.date} className={styles.distCol}>
            <div className={styles.distStack}>
              {d.segments.map((s) => (
                <div
                  key={s.fam}
                  className={styles.distSeg}
                  style={{ height: `${s.pct}%`, background: colorFor(s.fam) }}
                  onMouseMove={(e) => setHover({ x: e.clientX, y: e.clientY, date: d.date, fam: s.fam, pct: s.pct, value: s.value })}
                  onMouseLeave={() => setHover(null)}
                />
              ))}
            </div>
            <span className={styles.distLabel}>
              {idx % labelEvery === 0 ? `${d.date.slice(8)}/${d.date.slice(5, 7)}` : ''}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.distLegend}>
        {familyNames.map((fam) => (
          <span key={fam} className={styles.distLegendItem}>
            <span className={styles.curveDot} style={{ background: colorFor(fam) }} />
            <span className={styles.distLegendName}>{fam}</span>
          </span>
        ))}
      </div>

      {hover && (
        <div className={styles.curveTooltip} style={{ left: hover.x + 14, top: hover.y + 14 }}>
          <div className={styles.curveTooltipLabel} style={{ textTransform: 'capitalize' }}>{hover.fam}</div>
          <div className={styles.curveTooltipTime}>{hover.date}</div>
          <div className={styles.curveTooltipRow}><span>Part</span><strong>{hover.pct.toFixed(0)}%</strong></div>
          <div className={styles.curveTooltipRow}><span>Joueurs (moy.)</span><strong>{Math.round(hover.value)}</strong></div>
        </div>
      )}
    </div>
  );
}

function formatPlaytime(minutes: number): string {
  if (minutes >= 1440) {
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    return `${days.toLocaleString('fr-FR')}j ${hours}h`;
  }
  if (minutes >= 60) {
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  }
  return `${minutes}m`;
}

/** Variation en % vs période précédente ; null si pas de base (précédent = 0). */
function pctChange(current: number, previous: number): number | null {
  if (previous <= 0) return current > 0 ? null : 0;
  return ((current - previous) / previous) * 100;
}

function ChangeBadge({ current, previous }: { current: number; previous: number }) {
  const pct = pctChange(current, previous);
  if (pct === null) {
    return <span className={styles.changeNew}>nouveau</span>;
  }
  const up = pct >= 0;
  return (
    <span className={up ? styles.changeUp : styles.changeDown}>
      {up ? <TrendingUp size={12} strokeWidth={2.5} /> : <TrendingDown size={12} strokeWidth={2.5} />}
      {Math.abs(pct).toFixed(1)}% vs préc.
    </span>
  );
}

function ActivityCard({
  icon: Icon,
  label,
  current,
  previous,
  format,
  sub,
}: {
  icon: LucideIcon;
  label: string;
  current: number;
  previous?: number;
  format?: (n: number) => string;
  sub?: string;
}) {
  return (
    <div className={styles.summaryCard}>
      <div className={styles.cardIcon}><Icon size={24} strokeWidth={2} /></div>
      <div className={styles.cardContent}>
        <span className={styles.cardValue}>{format ? format(current) : current.toLocaleString('fr-FR')}</span>
        <span className={styles.cardLabel}>{label}</span>
        <span className={styles.cardSub}>
          {previous !== undefined ? <ChangeBadge current={current} previous={previous} /> : sub}
        </span>
      </div>
    </div>
  );
}

function ActiviteSection() {
  const [range, setRange] = useState<ActivityRange>(7);
  const [customStart, setCustomStart] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return formatYMD(d);
  });
  const [customEnd, setCustomEnd] = useState<string>(() => formatYMD(new Date()));
  const [data, setData] = useState<PlayerActivityResponse | null>(null);
  const [series, setSeries] = useState<DaySeriesDTO[]>([]);
  const [totalPlayers, setTotalPlayers] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService.getTotalPlayers().then((r) => setTotalPlayers(r.data?.total ?? null));
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, customStart, customEnd]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    const [res, serRes] = await Promise.all([
      range === 'custom'
        ? apiService.getPlayerActivity(undefined, customStart, customEnd)
        : apiService.getPlayerActivity(range),
      range === 'custom'
        ? apiService.getPlayerActivitySeries(undefined, customStart, customEnd)
        : apiService.getPlayerActivitySeries(range),
    ]);
    if (res.error) {
      setError(res.error);
    } else if (res.data) {
      setData(res.data);
    }
    setSeries(serRes.data?.days ?? []);
    setLoading(false);
  };

  const cur = data?.current;
  const prev = data?.previous;

  const days = range === 'custom'
    ? Math.max(1, Math.round((new Date(customEnd).getTime() - new Date(customStart).getTime()) / 86400000) + 1)
    : range;
  const durationMinutes = durationForDays(days);

  return (
    <div className={styles.economyTab}>
      <div className={styles.activityToolbar}>
        <select
          className={styles.daysSelect}
          value={String(range)}
          onChange={(e) => {
            const v = e.target.value;
            setRange(v === 'custom' ? 'custom' : (Number(v) as ActivityRange));
          }}
        >
          <option value="7">7 derniers jours</option>
          <option value="14">14 derniers jours</option>
          <option value="30">30 derniers jours</option>
          <option value="60">60 derniers jours</option>
          <option value="custom">Personnalisé</option>
        </select>
        {range === 'custom' && (
          <>
            <input type="date" className={styles.filterInput} value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
            <input type="date" className={styles.filterInput} value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
          </>
        )}
        {data && <span className={styles.rangeDisplay}>{data.startDate} → {data.endDate}</span>}
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <p>Chargement de l'activité...</p>
        </div>
      ) : error ? (
        <div className={styles.errorState}>
          <span className={styles.errorIcon}><AlertCircle size={32} strokeWidth={2} /></span>
          <h3>Erreur de chargement</h3>
          <p>{error}</p>
          <button className={styles.retryButton} onClick={loadData}>Réessayer</button>
        </div>
      ) : cur && prev ? (
        <>
          <div className={styles.summaryCards}>
            {totalPlayers !== null && (
              <ActivityCard icon={Users} label="Joueurs uniques (total)" current={totalPlayers} sub="tous temps confondus" />
            )}
            <ActivityCard icon={Users} label="Joueurs uniques (période)" current={cur.uniquePlayers} previous={prev.uniquePlayers} />
            <ActivityCard icon={UserPlus} label="Nouveaux joueurs" current={cur.newPlayers} previous={prev.newPlayers} />
            <ActivityCard icon={Clock} label="Temps de jeu total" current={cur.totalMinutes} previous={prev.totalMinutes} format={formatPlaytime} />
          </div>
        </>
      ) : null}

      <OnlineCurves durationMinutes={durationMinutes} activitySeries={series} />
    </div>
  );
}

// ==================== ECONOMIE SECTION ====================

function formatYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Lundi de la semaine en cours (format YYYY-MM-DD, heure locale). */
function currentWeekStart(): string {
  const now = new Date();
  const dow = now.getDay(); // 0=dimanche..6=samedi
  const daysSinceMonday = dow === 0 ? 6 : dow - 1;
  const monday = new Date(now);
  monday.setDate(now.getDate() - daysSinceMonday);
  return formatYMD(monday);
}

function EconomieSection() {
  const [currentStats, setCurrentStats] = useState<EconomyCurrentResponse | null>(null);
  const [breakdown, setBreakdown] = useState<EconomyBreakdownResponse | null>(null);
  const [history, setHistory] = useState<EconomySnapshotDTO[]>([]);
  const [weekGive, setWeekGive] = useState(0);
  const [weekClear, setWeekClear] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState(30);

  useEffect(() => {
    loadData();
  }, [selectedDays]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    const weekStart = currentWeekStart();
    const today = formatYMD(new Date());

    const [statsResult, breakdownResult, historyResult, giveResult, clearResult] = await Promise.all([
      apiService.getEconomyStats(),
      apiService.getEconomyBreakdown(),
      apiService.getEconomyHistory(selectedDays),
      apiService.getVaultTransactionStats(weekStart, today, 'give', STATS_REASON),
      apiService.getVaultTransactionStats(weekStart, today, 'clear', STATS_REASON),
    ]);

    if (statsResult.error) {
      setError(statsResult.error);
    } else if (statsResult.data) {
      setCurrentStats(statsResult.data);
    }

    if (breakdownResult.data) {
      setBreakdown(breakdownResult.data);
    }

    if (historyResult.data) {
      setHistory(historyResult.data.snapshots);
    }

    const sumDays = (r: { data?: { days: DailyTransactionStatsDTO[] } }) =>
      r.data?.days?.reduce((acc, d) => acc + d.total, 0) ?? 0;
    setWeekGive(sumDays(giveResult));
    setWeekClear(sumDays(clearResult));

    setLoading(false);
  };

  const formatMoney = (amount: number): string => {
    if (amount >= 1_000_000_000) {
      return `${(amount / 1_000_000_000).toFixed(2)}B`;
    }
    if (amount >= 1_000_000) {
      return `${(amount / 1_000_000).toFixed(2)}M`;
    }
    if (amount >= 1_000) {
      return `${(amount / 1_000).toFixed(1)}K`;
    }
    return amount.toLocaleString('fr-FR');
  };

  const formatFullMoney = (amount: number): string => {
    return amount.toLocaleString('fr-FR') + ' $';
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <p>Chargement des statistiques...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <span className={styles.errorIcon}><AlertCircle size={32} strokeWidth={2} /></span>
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
        <button className={styles.retryButton} onClick={loadData}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className={styles.economyTab}>
      {/* Summary Cards */}
      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}><Coins size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardValue}>{formatFullMoney(currentStats?.totalMoney || 0)}</span>
            <span className={styles.cardLabel}>Monnaie en circulation</span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}><BarChart3 size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardValue}>{formatFullMoney(currentStats?.averageBalance || 0)}</span>
            <span className={styles.cardLabel}>Balance moyenne</span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}><Scale size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardValue}>{formatFullMoney(breakdown?.medianBalance || 0)}</span>
            <span className={styles.cardLabel}>Argent médian</span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}><Activity size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardValue}>{formatFullMoney(breakdown?.activeMoney || 0)}</span>
            <span className={styles.cardLabel}>Argent actif</span>
            <span className={styles.cardSub}>{(breakdown?.activePlayers || 0).toLocaleString('fr-FR')} joueurs actifs (30j)</span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}><PiggyBank size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardValue}>{formatFullMoney(breakdown?.dormantMoney || 0)}</span>
            <span className={styles.cardLabel}>Argent dormant</span>
            <span className={styles.cardSub}>{(breakdown?.dormantPlayers || 0).toLocaleString('fr-FR')} joueurs inactifs</span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={`${styles.cardIcon} ${styles.cardIconGive}`}><TrendingUp size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardValue}>{formatFullMoney(weekGive)}</span>
            <span className={styles.cardLabel}>Give (cette semaine)</span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={`${styles.cardIcon} ${styles.cardIconClear}`}><TrendingDown size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardValue}>{formatFullMoney(weekClear)}</span>
            <span className={styles.cardLabel}>Clear (cette semaine)</span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}><Wallet size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span
              className={styles.cardValue}
              style={{ color: weekGive - weekClear >= 0 ? '#16A34A' : '#DC2626' }}
            >
              {weekGive - weekClear >= 0 ? '+' : '−'}{formatFullMoney(Math.abs(weekGive - weekClear))}
            </span>
            <span className={styles.cardLabel}>Bilan (cette semaine)</span>
            <span className={styles.cardSub}>
              {weekGive - weekClear >= 0 ? 'Injection nette' : 'Retrait net'} (give − clear)
            </span>
          </div>
        </div>
      </div>

      {/* Update notice */}
      <div className={styles.updateNotice}>
        <span className={styles.updateIcon}><RefreshCw size={16} strokeWidth={2} /></span>
        <span>Les snapshots sont enregistrés toutes les 24h à minuit</span>
        {currentStats?.lastSnapshotDate && (
          <span className={styles.lastUpdate}>
            Dernier snapshot : {currentStats.lastSnapshotDate}
          </span>
        )}
      </div>

      {/* Chart Section */}
      <div className={styles.chartSection}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>Évolution de la monnaie en circulation</h3>
          <div className={styles.chartControls}>
            <select
              className={styles.daysSelect}
              value={selectedDays}
              onChange={(e) => setSelectedDays(Number(e.target.value))}
            >
              <option value={7}>7 jours</option>
              <option value={14}>14 jours</option>
              <option value={30}>30 jours</option>
              <option value={60}>60 jours</option>
              <option value={90}>90 jours</option>
            </select>
          </div>
        </div>

        {history.length === 0 ? (
          <div className={styles.noData}>
            <span className={styles.noDataIcon}><TrendingUp size={32} strokeWidth={2} /></span>
            <p>Aucune donnée disponible</p>
            <span className={styles.noDataHint}>
              Les données apparaîtront après le premier snapshot à minuit.
            </span>
          </div>
        ) : (
          <EconomyChart data={history} formatMoney={formatMoney} formatDate={formatDate} />
        )}
      </div>

      {/* Détail des mouvements d'argent (give / clear par jour) */}
      <TransactionsTab />
    </div>
  );
}

// ==================== CHART COMPONENT ====================

interface ChartProps {
  data: EconomySnapshotDTO[];
  formatMoney: (amount: number) => string;
  formatDate: (dateStr: string) => string;
}

function EconomyChart({ data, formatMoney, formatDate }: ChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState({ width: 800, height: 300 });

  // Measure chart size
  useEffect(() => {
    const updateSize = () => {
      if (chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        setChartSize({ width: rect.width, height: rect.height });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const chartData = useMemo(() => {
    if (data.length === 0) return { points: [], minY: 0, maxY: 0, yLabels: [] };

    const values = data.map(d => d.totalMoney);
    const minY = Math.min(...values);
    const maxY = Math.max(...values);

    // Add some padding to min/max
    const range = maxY - minY || maxY * 0.1;
    const paddedMin = Math.max(0, minY - range * 0.1);
    const paddedMax = maxY + range * 0.1;

    // Generate Y axis labels (5 labels)
    const yLabels: number[] = [];
    for (let i = 0; i <= 4; i++) {
      yLabels.push(paddedMin + (paddedMax - paddedMin) * (i / 4));
    }

    // Calculate point positions in pixels
    const points = data.map((d, i) => {
      const x = data.length > 1 ? (i / (data.length - 1)) * chartSize.width : chartSize.width / 2;
      const yPercent = paddedMax > paddedMin
        ? ((d.totalMoney - paddedMin) / (paddedMax - paddedMin))
        : 0.5;
      const y = chartSize.height - (yPercent * chartSize.height);
      return { x, y, xPercent: (i / (data.length - 1)) * 100, yPercent: (1 - yPercent) * 100, data: d };
    });

    return { points, minY: paddedMin, maxY: paddedMax, yLabels: yLabels.reverse() };
  }, [data, chartSize]);

  // Generate smooth SVG path using actual pixel coordinates
  const linePath = useMemo(() => {
    if (chartData.points.length === 0) return '';

    return chartData.points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ');
  }, [chartData.points]);

  // Generate gradient area path
  const areaPath = useMemo(() => {
    if (chartData.points.length === 0) return '';

    const linePoints = chartData.points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ');

    const lastPoint = chartData.points[chartData.points.length - 1];
    const firstPoint = chartData.points[0];

    if (!lastPoint || !firstPoint) return '';
    return `${linePoints} L ${lastPoint.x.toFixed(2)} ${chartSize.height} L ${firstPoint.x.toFixed(2)} ${chartSize.height} Z`;
  }, [chartData.points, chartSize.height]);

  // Find closest point on hover
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!chartRef.current || chartData.points.length === 0) return;

    const rect = chartRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;

    // Find closest point
    let closestIndex = 0;
    let closestDistance = Infinity;

    chartData.points.forEach((point, i) => {
      const distance = Math.abs(point.x - mouseX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    setHoveredIndex(closestIndex);
  };

  return (
    <div className={styles.chart}>
      {/* Y Axis Labels */}
      <div className={styles.yAxis}>
        {chartData.yLabels.map((label, i) => (
          <span key={i} className={styles.yLabel}>{formatMoney(label)}</span>
        ))}
      </div>

      {/* Chart Area */}
      <div
        ref={chartRef}
        className={styles.chartArea}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <svg
          width={chartSize.width}
          height={chartSize.height}
          className={styles.chartSvg}
        >
          {/* Grid lines */}
          <g className={styles.gridLines}>
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
              <line key={i} x1="0" y1={ratio * chartSize.height} x2={chartSize.width} y2={ratio * chartSize.height} />
            ))}
          </g>

          {/* Gradient fill */}
          <defs>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(99, 102, 241, 0.25)" />
              <stop offset="100%" stopColor="rgba(99, 102, 241, 0)" />
            </linearGradient>
          </defs>

          {/* Area fill */}
          <path d={areaPath} fill="url(#areaGradient)" />

          {/* Line */}
          <path
            d={linePath}
            fill="none"
            stroke="#6366F1"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Hover indicator */}
          {hoveredIndex !== null && chartData.points[hoveredIndex] && (
            <>
              {/* Vertical line */}
              <line
                x1={chartData.points[hoveredIndex].x}
                y1={0}
                x2={chartData.points[hoveredIndex].x}
                y2={chartSize.height}
                stroke="rgba(99, 102, 241, 0.3)"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              {/* Point */}
              <circle
                cx={chartData.points[hoveredIndex].x}
                cy={chartData.points[hoveredIndex].y}
                r="6"
                fill="#6366F1"
                stroke="white"
                strokeWidth="2"
              />
            </>
          )}
        </svg>

      </div>

      {/* Hover tooltip - outside chartArea to avoid overflow:hidden */}
      {hoveredIndex !== null && chartData.points[hoveredIndex] && (() => {
        const point = chartData.points[hoveredIndex];
        const isRight = point.xPercent > 75;
        const isTop = point.yPercent < 30; // If point is in top 30%, show tooltip below

        const tooltipClasses = [
          styles.tooltip,
          isRight ? styles.tooltipLeft : '',
          isTop ? styles.tooltipBelow : '',
        ].filter(Boolean).join(' ');

        return (
          <div
            className={tooltipClasses}
            style={{
              left: `calc(60px + ${point.x}px + ${isRight ? '0px' : '0px'})`,
              top: `${point.y}px`,
            }}
          >
            <span className={styles.tooltipDate}>
              {formatDate(point.data.date)}
            </span>
            <span className={styles.tooltipValue}>
              {point.data.totalMoney.toLocaleString('fr-FR')} $
            </span>
            <span className={styles.tooltipPlayers}>
              {point.data.playerCount.toLocaleString('fr-FR')} joueurs
            </span>
          </div>
        );
      })()}

      {/* X Axis Labels */}
      <div className={styles.xAxis}>
        {data.length <= 10 ? (
          // Show all dates if 10 or fewer
          data.map((d, i) => (
            <span key={i} className={styles.xLabel} style={{ left: `${chartData.points[i]?.xPercent || 0}%` }}>
              {formatDate(d.date)}
            </span>
          ))
        ) : (
          // Show ~7 evenly spaced dates
          Array.from({ length: 7 }, (_, i) => {
            const index = Math.round((i / 6) * (data.length - 1));
            const dataPoint = data[index];
            return (
              <span key={i} className={styles.xLabel} style={{ left: `${(i / 6) * 100}%` }}>
                {dataPoint ? formatDate(dataPoint.date) : ''}
              </span>
            );
          })
        )}
      </div>
    </div>
  );
}

// ==================== STORE POINTS TAB ====================

function StorePointsTab() {
  const [currentStats, setCurrentStats] = useState<StorePointsCurrentResponse | null>(null);
  const [history, setHistory] = useState<StorePointsSnapshotDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState(30);

  useEffect(() => {
    loadData();
  }, [selectedDays]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    const [statsResult, historyResult] = await Promise.all([
      apiService.getStorePointsStats(),
      apiService.getStorePointsHistory(selectedDays),
    ]);

    if (statsResult.error) {
      setError(statsResult.error);
    } else if (statsResult.data) {
      setCurrentStats(statsResult.data);
    }

    if (historyResult.data) {
      setHistory(historyResult.data.snapshots);
    }

    setLoading(false);
  };

  const formatPoints = (amount: number): string => {
    if (amount >= 1_000_000_000) {
      return `${(amount / 1_000_000_000).toFixed(2)}B`;
    }
    if (amount >= 1_000_000) {
      return `${(amount / 1_000_000).toFixed(2)}M`;
    }
    if (amount >= 1_000) {
      return `${(amount / 1_000).toFixed(1)}K`;
    }
    return amount.toLocaleString('fr-FR');
  };

  const formatFullPoints = (amount: number): string => {
    return amount.toLocaleString('fr-FR') + ' PB';
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <p>Chargement des statistiques...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <span className={styles.errorIcon}><AlertCircle size={32} strokeWidth={2} /></span>
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
        <button className={styles.retryButton} onClick={loadData}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className={`${styles.economyTab} ${styles.storePointsTab}`}>
      {/* Summary Cards */}
      <div className={styles.summaryCards}>
        <div className={`${styles.summaryCard} ${styles.storePointsCard}`}>
          <div className={styles.cardIcon}><Gem size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardValue}>{formatFullPoints(currentStats?.totalPoints || 0)}</span>
            <span className={styles.cardLabel}>Points en circulation</span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}><Users size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardValue}>{(currentStats?.playerCount || 0).toLocaleString('fr-FR')}</span>
            <span className={styles.cardLabel}>Joueurs avec points</span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.cardIcon}><BarChart3 size={24} strokeWidth={2} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardValue}>{formatFullPoints(Math.round(currentStats?.averagePoints || 0))}</span>
            <span className={styles.cardLabel}>Points moyens</span>
          </div>
        </div>
      </div>

      {/* Update notice */}
      <div className={styles.updateNotice}>
        <span className={styles.updateIcon}><RefreshCw size={16} strokeWidth={2} /></span>
        <span>Les snapshots sont enregistrés toutes les 24h à minuit</span>
        {currentStats?.lastSnapshotDate && (
          <span className={styles.lastUpdate}>
            Dernier snapshot : {currentStats.lastSnapshotDate}
          </span>
        )}
      </div>

      {/* Chart Section */}
      <div className={styles.chartSection}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>Évolution des points boutique en circulation</h3>
          <div className={styles.chartControls}>
            <select
              className={styles.daysSelect}
              value={selectedDays}
              onChange={(e) => setSelectedDays(Number(e.target.value))}
            >
              <option value={7}>7 jours</option>
              <option value={14}>14 jours</option>
              <option value={30}>30 jours</option>
              <option value={60}>60 jours</option>
              <option value={90}>90 jours</option>
            </select>
          </div>
        </div>

        {history.length === 0 ? (
          <div className={styles.noData}>
            <span className={styles.noDataIcon}><TrendingUp size={32} strokeWidth={2} /></span>
            <p>Aucune donnée disponible</p>
            <span className={styles.noDataHint}>
              Les données apparaîtront après le premier snapshot à minuit.
            </span>
          </div>
        ) : (
          <StorePointsChart data={history} formatPoints={formatPoints} formatDate={formatDate} />
        )}
      </div>
    </div>
  );
}

// ==================== STORE POINTS CHART COMPONENT ====================

interface StorePointsChartProps {
  data: StorePointsSnapshotDTO[];
  formatPoints: (amount: number) => string;
  formatDate: (dateStr: string) => string;
}

// ==================== TRANSACTIONS TAB ====================

// Helper to format date as YYYY-MM-DD
const formatDateYMD = (date: Date): string => {
  return date.toISOString().slice(0, 10);
};

function TransactionsTab() {
  const [clearData, setClearData] = useState<DailyTransactionStatsDTO[]>([]);
  const [giveData, setGiveData] = useState<DailyTransactionStatsDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Date range state (default: last 7 days)
  const [dateRange, setDateRange] = useState<'7d' | '14d' | '30d' | 'custom'>('7d');
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return formatDateYMD(d);
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return formatDateYMD(new Date());
  });

  // Update dates when range changes
  useEffect(() => {
    const end = new Date();
    const start = new Date();

    switch (dateRange) {
      case '7d':
        start.setDate(end.getDate() - 6);
        break;
      case '14d':
        start.setDate(end.getDate() - 13);
        break;
      case '30d':
        start.setDate(end.getDate() - 29);
        break;
      case 'custom':
        return; // Don't update for custom
    }

    setStartDate(formatDateYMD(start));
    setEndDate(formatDateYMD(end));
  }, [dateRange]);

  // Load data when dates change
  useEffect(() => {
    loadData();
  }, [startDate, endDate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    const [clearResult, giveResult] = await Promise.all([
      apiService.getVaultTransactionStats(startDate, endDate, 'clear', STATS_REASON),
      apiService.getVaultTransactionStats(startDate, endDate, 'give', STATS_REASON),
    ]);

    if (clearResult.error) {
      setError(clearResult.error);
    } else if (clearResult.data) {
      setClearData(clearResult.data.days);
    }

    if (giveResult.data) {
      setGiveData(giveResult.data.days);
    }

    setLoading(false);
  };

  const formatDateRange = (): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
    return `${start.toLocaleDateString('fr-FR', options)} - ${end.toLocaleDateString('fr-FR', options)}`;
  };

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <p>Chargement des transactions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <span className={styles.errorIcon}><AlertCircle size={32} strokeWidth={2} /></span>
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
        <button className={styles.retryButton} onClick={loadData}>
          Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className={styles.transactionsTab}>
      <div className={styles.chartSection}>
        {/* En-tête du bloc avec le contrôle de période intégré */}
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>
            <span className={styles.chartIcon}><BarChart3 size={18} strokeWidth={2} /></span>
            Mouvements d'argent staff (give / clear)
          </h3>
          <div className={styles.chartControls}>
            <select
              className={styles.daysSelect}
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
            >
              <option value="7d">7 derniers jours</option>
              <option value="14d">14 derniers jours</option>
              <option value="30d">30 derniers jours</option>
              <option value="custom">Personnalisé</option>
            </select>
            {dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  className={styles.filterInput}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <input
                  type="date"
                  className={styles.filterInput}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </>
            )}
          </div>
        </div>

        <div className={styles.rangeDisplay}>{formatDateRange()}</div>

        {/* Clear (négatif) */}
        <h4 className={styles.subChartTitle}>
          <span className={styles.chartIcon}><TrendingDown size={16} strokeWidth={2} /></span>
          Clear d'argent par jour
        </h4>
        <StackedBarChart data={clearData} isNegative={true} />

        {/* Give (positif) */}
        <h4 className={styles.subChartTitle}>
          <span className={styles.chartIcon}><TrendingUp size={16} strokeWidth={2} /></span>
          Give d'argent par jour
        </h4>
        <StackedBarChart data={giveData} isNegative={false} />
      </div>
    </div>
  );
}

function StorePointsChart({ data, formatPoints, formatDate }: StorePointsChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState({ width: 800, height: 300 });

  // Measure chart size
  useEffect(() => {
    const updateSize = () => {
      if (chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        setChartSize({ width: rect.width, height: rect.height });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const chartData = useMemo(() => {
    if (data.length === 0) return { points: [], minY: 0, maxY: 0, yLabels: [] };

    const values = data.map(d => d.totalPoints);
    const minY = Math.min(...values);
    const maxY = Math.max(...values);

    // Add some padding to min/max
    const range = maxY - minY || maxY * 0.1;
    const paddedMin = Math.max(0, minY - range * 0.1);
    const paddedMax = maxY + range * 0.1;

    // Generate Y axis labels (5 labels)
    const yLabels: number[] = [];
    for (let i = 0; i <= 4; i++) {
      yLabels.push(paddedMin + (paddedMax - paddedMin) * (i / 4));
    }

    // Calculate point positions in pixels
    const points = data.map((d, i) => {
      const x = data.length > 1 ? (i / (data.length - 1)) * chartSize.width : chartSize.width / 2;
      const yPercent = paddedMax > paddedMin
        ? ((d.totalPoints - paddedMin) / (paddedMax - paddedMin))
        : 0.5;
      const y = chartSize.height - (yPercent * chartSize.height);
      return { x, y, xPercent: (i / (data.length - 1)) * 100, yPercent: (1 - yPercent) * 100, data: d };
    });

    return { points, minY: paddedMin, maxY: paddedMax, yLabels: yLabels.reverse() };
  }, [data, chartSize]);

  // Generate smooth SVG path using actual pixel coordinates
  const linePath = useMemo(() => {
    if (chartData.points.length === 0) return '';

    return chartData.points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ');
  }, [chartData.points]);

  // Generate gradient area path
  const areaPath = useMemo(() => {
    if (chartData.points.length === 0) return '';

    const linePoints = chartData.points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ');

    const lastPoint = chartData.points[chartData.points.length - 1];
    const firstPoint = chartData.points[0];

    if (!lastPoint || !firstPoint) return '';
    return `${linePoints} L ${lastPoint.x.toFixed(2)} ${chartSize.height} L ${firstPoint.x.toFixed(2)} ${chartSize.height} Z`;
  }, [chartData.points, chartSize.height]);

  // Find closest point on hover
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!chartRef.current || chartData.points.length === 0) return;

    const rect = chartRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;

    // Find closest point
    let closestIndex = 0;
    let closestDistance = Infinity;

    chartData.points.forEach((point, i) => {
      const distance = Math.abs(point.x - mouseX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    setHoveredIndex(closestIndex);
  };

  return (
    <div className={styles.chart}>
      {/* Y Axis Labels */}
      <div className={styles.yAxis}>
        {chartData.yLabels.map((label, i) => (
          <span key={i} className={styles.yLabel}>{formatPoints(label)}</span>
        ))}
      </div>

      {/* Chart Area */}
      <div
        ref={chartRef}
        className={styles.chartArea}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <svg
          width={chartSize.width}
          height={chartSize.height}
          className={styles.chartSvg}
        >
          {/* Grid lines */}
          <g className={styles.gridLines}>
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
              <line key={i} x1="0" y1={ratio * chartSize.height} x2={chartSize.width} y2={ratio * chartSize.height} />
            ))}
          </g>

          {/* Gradient fill - cyan/teal color for store points */}
          <defs>
            <linearGradient id="storePointsAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(6, 182, 212, 0.25)" />
              <stop offset="100%" stopColor="rgba(6, 182, 212, 0)" />
            </linearGradient>
          </defs>

          {/* Area fill */}
          <path d={areaPath} fill="url(#storePointsAreaGradient)" />

          {/* Line - cyan/teal color */}
          <path
            d={linePath}
            fill="none"
            stroke="#06B6D4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Hover indicator */}
          {hoveredIndex !== null && chartData.points[hoveredIndex] && (
            <>
              {/* Vertical line */}
              <line
                x1={chartData.points[hoveredIndex].x}
                y1={0}
                x2={chartData.points[hoveredIndex].x}
                y2={chartSize.height}
                stroke="rgba(6, 182, 212, 0.3)"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              {/* Point */}
              <circle
                cx={chartData.points[hoveredIndex].x}
                cy={chartData.points[hoveredIndex].y}
                r="6"
                fill="#06B6D4"
                stroke="white"
                strokeWidth="2"
              />
            </>
          )}
        </svg>

      </div>

      {/* Hover tooltip - outside chartArea to avoid overflow:hidden */}
      {hoveredIndex !== null && chartData.points[hoveredIndex] && (() => {
        const point = chartData.points[hoveredIndex];
        const isRight = point.xPercent > 75;
        const isTop = point.yPercent < 30;

        const tooltipClasses = [
          styles.tooltip,
          styles.tooltipCyan,
          isRight ? styles.tooltipLeft : '',
          isTop ? styles.tooltipBelow : '',
        ].filter(Boolean).join(' ');

        return (
          <div
            className={tooltipClasses}
            style={{
              left: `calc(60px + ${point.x}px + ${isRight ? '0px' : '0px'})`,
              top: `${point.y}px`,
            }}
          >
            <span className={styles.tooltipDate}>
              {formatDate(point.data.date)}
            </span>
            <span className={styles.tooltipValue}>
              {point.data.totalPoints.toLocaleString('fr-FR')} PB
            </span>
            <span className={styles.tooltipPlayers}>
              {point.data.playerCount.toLocaleString('fr-FR')} joueurs
            </span>
          </div>
        );
      })()}

      {/* X Axis Labels */}
      <div className={styles.xAxis}>
        {data.length <= 10 ? (
          // Show all dates if 10 or fewer
          data.map((d, i) => (
            <span key={i} className={styles.xLabel} style={{ left: `${chartData.points[i]?.xPercent || 0}%` }}>
              {formatDate(d.date)}
            </span>
          ))
        ) : (
          // Show ~7 evenly spaced dates
          Array.from({ length: 7 }, (_, i) => {
            const index = Math.round((i / 6) * (data.length - 1));
            const dataPoint = data[index];
            return (
              <span key={i} className={styles.xLabel} style={{ left: `${(i / 6) * 100}%` }}>
                {dataPoint ? formatDate(dataPoint.date) : ''}
              </span>
            );
          })
        )}
      </div>
    </div>
  );
}
